import { useEffect, useState, useCallback, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Iter 69.4: Context per aprire il modale Timeline giocatore da qualsiasi punto
 * dentro una lega. Il Provider viene installato in LeagueLayout, il consumer è
 * tipicamente PlayerNameLink.
 */
const PlayerTimelineCtx = createContext(null);
export const usePlayerTimeline = () => useContext(PlayerTimelineCtx);
export const PlayerTimelineProvider = PlayerTimelineCtx.Provider;

/**
 * Iter 68: Hook riutilizzabile per risolvere i nomi giocatore in ID rooster
 * (usato per rendere i nomi cliccabili verso la pagina storica).
 *
 * Uso:
 *   const roosterMap = useRoosterResolve(playerNames);
 *   ...
 *   <PlayerNameLink name={p.name} roosterMap={roosterMap} />
 *
 * @param {string[]} names - Array di nomi giocatore da risolvere
 * @returns {Object} - Mappa { name: rooster_id }
 */
export function useRoosterResolve(names) {
  const [map, setMap] = useState({});

  useEffect(() => {
    if (!names || names.length === 0) return;
    const unique = Array.from(new Set(names.filter(Boolean)));
    if (unique.length === 0) return;
    let cancelled = false;
    api.post("/magnati/rooster/resolve-batch", { names: unique })
      .then(r => { if (!cancelled) setMap(r.data?.map || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(names || [])]);

  return map;
}

/**
 * Link cliccabile al nome del giocatore.
 * Iter 69.4: se il player ha `fantaId` (da serie_a_rooster o storico), il click
 * apre il modale Timeline multi-stagione. Altrimenti fallback su navigazione a
 * `/storico-magnati/giocatore/{roosterId}` (magnati_rooster / carriera lunga).
 */
export function PlayerNameLink({ name, roosterMap, fantaId, className = "", testid, target = "_self" }) {
  const timeline = usePlayerTimeline();
  const roosterId = roosterMap?.[name];

  // Priorità 1: fanta_id OPPURE roosterId + provider disponibile → apri Timeline modale
  // (Il modale supporta entrambi come tab.)
  if ((fantaId || roosterId) && timeline?.open) {
    return (
      <button type="button" onClick={(ev) => { ev.stopPropagation(); timeline.open(fantaId, name, roosterId); }}
        data-testid={testid || `player-timeline-link-${fantaId || roosterId}`}
        title="Apri storia giocatore (Serie A + Fanta Club)"
        className={`hover:text-purple-300 underline decoration-purple-400/40 decoration-dotted underline-offset-4 transition-colors bg-transparent border-0 p-0 cursor-pointer text-left ${className}`}>
        {name}
      </button>
    );
  }

  // Fallback (fuori da una lega, quindi nessun provider): naviga alla pagina storia
  if (roosterId) {
    return (
      <Link
        to={`/storico-magnati/giocatore/${roosterId}`}
        target={target}
        title="Apri storia carriera (squadre, prezzi, trasferimenti)"
        data-testid={testid || `player-history-link-${roosterId}`}
        className={`hover:text-amber-300 underline decoration-amber-500/30 decoration-dotted underline-offset-4 transition-colors ${className}`}>
        {name}
      </Link>
    );
  }

  return <span className={className} data-testid={testid}>{name}</span>;
}

/**
 * Utility per estrarre i nomi da una lista di player-like objects.
 * Accetta sia { name } che { player_name } che stringhe.
 */
export function extractNames(items, keyOrFn = "name") {
  if (!items) return [];
  const fn = typeof keyOrFn === "function" ? keyOrFn : (o) => o?.[keyOrFn];
  return items.map(fn).filter(Boolean);
}
